import { QueryClient } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import { useApiSuspenseInfiniteQuery } from '../../../hooks/useApiSuspenseInfiniteQuery';
import { useIntersectionObserver } from '../../../utilities/customHooks/useIntersectionObserver';
import { fetchTemplatesList } from '../api/logbookApi';
import { LogbookTemplatesPage } from '../types/loogBookTypes';

const getNextPageParam = (lastPage: LogbookTemplatesPage): number | undefined => {
    const { currentPage, totalPages } = lastPage;
    if (currentPage < totalPages) {
        return currentPage + 1;
    }
    return undefined;
};

const logBookTemplateQuery = (searchString: string, limit: number) => ({
    queryKey: ['logbookTemplates', searchString],
    queryFn: (props: any) => fetchTemplatesList({...props, limit}),
    getNextPageParam,
    initialPageParam: 1,
});

export const useLogbookTemplateTable = (searchString: string, limit = 20) => {
    const lastElementRef = useRef<HTMLElement>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const queryKey = ['logbookTemplates', searchString];
    const { data, ...queryResult } = useApiSuspenseInfiniteQuery(logBookTemplateQuery(searchString, limit));

    const { allTemplates, totalCount } = useMemo(() => {
        const all = data?.pages.flatMap(page => page.logbookTemplates) ?? [];
        const total = data?.pages[0]?.totalCount ?? 0;
        return { allTemplates: all, totalCount: total };
    }, [data]);

    useIntersectionObserver(
        tableContainerRef,
        lastElementRef,
        queryResult.fetchNextPage,
        queryResult.hasNextPage,
        queryResult.isFetchingNextPage
    );

    return {
        logbookTemplates: allTemplates,
        totalCount,
        lastElementRef,
        tableContainerRef,
        queryResult,
        queryKey,
    };
};

export const logbookTemplateLoader = (queryClient: QueryClient) => async ({ request }: { request: Request }) => {
    const url = new URL(request.url);
    const searchString = url.searchParams.get('search') || '';
    const limit = parseInt(url.searchParams.get('limit') || '20');
    const query = logBookTemplateQuery(searchString, limit);
    
    return (
      queryClient.getQueryData(query.queryKey) ??
      (await queryClient.fetchInfiniteQuery(query))
    );
  };